import { FCC, isSuccess, useToasts } from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { FormikHelpers } from "formik";
import { createContext, useCallback, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { eventTypeService } from "../shared/api/eventTypeService";
import { EventTypeBaseFormValues, EventTypeSaveRequest, EventTypeSettingsFormValues } from "../shared/types/eventType";
import { eventTypeValidation } from "./eventTypeValidation";

const mapFormValuesToCreateRequest = (
	baseData: EventTypeBaseFormValues,
	{
		costCategories,
		costCategoryRequirements,
		userResponsibilityRequirements,
		userResponsibilities,
		...settings
	}: EventTypeSettingsFormValues,
): EventTypeSaveRequest => {
	const { fallbackLanguage, image, eventRole, ...rest } = baseData;
	if (!fallbackLanguage || !eventRole) {
		throw new Error("Missing parameter detected");
	}

	if (costCategories.length !== costCategoryRequirements.length) {
		throw new Error("Incorrect mapping of cost category requirements detected");
	}

	if (userResponsibilities.length !== userResponsibilityRequirements.length) {
		throw new Error("Incorrect mapping of user responsibility requirements detected");
	}

	const feedbackIds = baseData.feedback.map((f) => f.id);

	return {
		...rest,
		...settings,
		eventRole,
		feedbackIds,
		fallbackLanguageIsoCode: fallbackLanguage.isoCode,
		imageId: !image || image?.id === -1 ? null : image.id,
		costCategoryRequirements: costCategoryRequirements.map((r) => ({
			costCategoryId: r.costCategory.id,
			requirement: r.requirement,
		})),
		userResponsibilityRequirements: userResponsibilityRequirements.map((r) => ({
			userResponsibilityId: r.userResponsibility.id,
			requirement: r.requirement,
		})),
		eventGoalRatingMetricWeight:
			settings.eventGoalRatingMetricRequirement === "prohibited" ? 0 : settings.eventGoalRatingMetricWeight,
	};
};

export type EventTypeSteps = "base" | "settings";

interface EventTypeState {
	setCurrentCreateStep: (step: EventTypeSteps) => void;
	currentCreateStep: EventTypeSteps;
	validateBaseData: (values: EventTypeBaseFormValues) => {
		[K in keyof EventTypeBaseFormValues]?: any;
	};
	baseData: EventTypeBaseFormValues | null;
	submitBaseData: (
		values: EventTypeBaseFormValues,
		onSubmitted: () => void,
		formikHelper: FormikHelpers<EventTypeBaseFormValues>,
	) => void;
	submitEventType: (values: EventTypeSettingsFormValues, onSubmitted: () => void) => Promise<boolean>;
	clearBaseData: () => void;
	setSettings: (values: EventTypeSettingsFormValues) => void;
	settings: EventTypeSettingsFormValues | null;
}

const EventTypeContext = createContext<EventTypeState | undefined>(undefined);

export const EventTypeProvider: FCC = ({ children }) => {
	const [baseData, setBaseData] = useState<EventTypeBaseFormValues | null>(null);
	const [settings, setSettings] = useState<EventTypeSettingsFormValues | null>(null);
	const [currentCreateStep, setCurrentCreateStep] = useState<EventTypeSteps>("base");
	const { t } = useTranslation();
	const { showToast } = useToasts();
	const queryClient = useQueryClient();

	const submitBaseData = useCallback(
		(
			values: EventTypeBaseFormValues,
			onSubmitted: () => void,
			formikHelper: FormikHelpers<EventTypeBaseFormValues>,
		) => {
			setBaseData(values);
			setCurrentCreateStep("settings");
			onSubmitted();
			formikHelper.setSubmitting(false);
		},
		[],
	);

	const validateBaseData = useCallback(
		(values: EventTypeBaseFormValues) => {
			return eventTypeValidation(t)(values);
		},
		[t],
	);

	const submitEventType = useCallback(
		async (values: EventTypeSettingsFormValues, onSubmitted: () => void) => {
			const res = await eventTypeService.store(
				mapFormValuesToCreateRequest(baseData as EventTypeBaseFormValues, values),
			);

			if (isSuccess(res)) {
				showToast({
					type: "success",
					title: t("Event type"),
					body: t("Event type successfully created."),
				});
				void queryClient.invalidateQueries({ queryKey: eventTypeService.listKey });
				onSubmitted();
				return true;
			}
			return false;
		},
		[baseData, queryClient, showToast, t],
	);

	const clearBaseData = useCallback(() => {
		setBaseData(null);
		setSettings(null);
		setCurrentCreateStep("base");
	}, []);

	return (
		<EventTypeContext.Provider
			value={{
				setCurrentCreateStep,
				currentCreateStep,
				baseData,
				setSettings,
				settings,
				submitBaseData,
				submitEventType,
				validateBaseData,
				clearBaseData,
			}}
		>
			{children}
		</EventTypeContext.Provider>
	);
};

export const useEventType = () => {
	const context = useContext(EventTypeContext);
	if (!context) {
		throw new Error("useEventType needs to be wrapped into EventTypeProvider");
	}

	return context;
};
